import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { AppStatusViewModel, UserProfileViewModel } from '@/common/viewModels';
import { UserContext } from '@/common/models';
import { UserMapper } from '@/common/mappers';

export interface IAppState {
  idToken: string;
  accessToken: string;
  tokensExpiry: string;
  applicationStatus: AppStatusViewModel;
  user: UserProfileViewModel;
  httpError: string;
  isLoadingCount: number;
  isDrawerOpen: boolean;
  isSidenavMini: boolean;
}

@Module({
  name: 'app',
  namespaced: true,
})
export default class AppStore extends VuexModule implements IAppState {
  // State
  applicationStatus: AppStatusViewModel = new AppStatusViewModel(false, '');
  idToken: string = sessionStorage.getItem('id_token') || '';
  accessToken: string = sessionStorage.getItem('access_token') || '';
  tokensExpiry: string = sessionStorage.getItem('expires_at') || '';
  user: UserProfileViewModel = new UserProfileViewModel();
  httpError: string = '';
  isLoadingCount: number = 0;
  isDrawerOpen: boolean = false;
  isSidenavMini: boolean = true;

  // Getters
  get hasLoggedIn(): boolean {
    return this.user.hasLoggedIn;
  }

  get myProfile(): UserProfileViewModel {
    return this.user;
  }

  get isLoading(): boolean {
    return this.applicationStatus.isLoading;
  }

  // Mutations
  @Mutation
  SET_ISLOADING(isLoading: boolean) {

    // Support for isLoading stack
    isLoading ?
      this.isLoadingCount++ :
      this.isLoadingCount--;

    this.applicationStatus.isLoading = this.isLoadingCount > 0;
  }

  @Mutation
  UPDATE_AUTH_TOKENS(authResult: any) {
    sessionStorage.setItem('access_token', authResult.accessToken);
    this.accessToken = authResult.accessToken;

    sessionStorage.setItem('id_token', authResult.idToken);
    this.idToken = authResult.idToken;

    // Set the time that the access token will expire at
    const expiresAt = JSON.stringify(
      authResult.expiresIn * 1000 + new Date().getTime(),
    );

    this.tokensExpiry = expiresAt;
    sessionStorage.setItem('expires_at', expiresAt);
  }

  @Mutation
  CLEAR_AUTH_TOKENS() {
    sessionStorage.removeItem('access_token');
    this.accessToken = '';

    sessionStorage.removeItem('id_token');
    this.idToken = '';

    sessionStorage.removeItem('expires_at');
    this.tokensExpiry = '';

    this.user = new UserProfileViewModel();
  }

  @Mutation
  SAVE_CURRENT_USER(userProfile: UserProfileViewModel) {
    this.user = userProfile;
  }

  @Mutation
  SET_APPLICATION_STATUS(appStatus: AppStatusViewModel) {
    this.applicationStatus = appStatus;
  }

  @Mutation
  SAVE_HTTP_ERROR(error: string) {
    this.httpError = error;
  }

  @Mutation
  CLEAR_HTTP_ERROR() {
    this.httpError = '';
  }

  @Mutation
  TOGGLE_IS_DRAWER_OPEN() {
    this.isDrawerOpen = !this.isDrawerOpen;
  }

  @Mutation
  SET_IS_DRAWER_OPEN(isOpen: boolean) {
    this.isDrawerOpen = isOpen;
  }

  @Mutation
  TOGGLE_IS_SIDENAV_MINI() {
    this.isSidenavMini = !this.isSidenavMini;
  }

  @Mutation
  SET_IS_SIDENAV_MINI(isMini: boolean) {
    this.isSidenavMini = isMini;
  }

  // Actions
  @Action
  setIsLoading(isLoading: boolean) {
    this.SET_ISLOADING(isLoading);
  }

  @Action
  setSession(authResult: any) {
    this.UPDATE_AUTH_TOKENS(authResult);
  }
  @Action
  logout() {
    this.CLEAR_AUTH_TOKENS();
  }

  @Action
  saveCurrentContext(currentUserContext: UserContext) {
    this.SAVE_CURRENT_USER(UserMapper.convertToUserProfileViewModelFromUserContext(currentUserContext));
  }

  @Action
  setApplicationStatus(appStatus: AppStatusViewModel) {
    this.SET_APPLICATION_STATUS(appStatus);
  }

  @Action
  saveCurrentUser(userProfile: UserProfileViewModel) {
    this.SAVE_CURRENT_USER(userProfile);
  }

  @Action
  handleHttpError(error: string) {
    this.SAVE_HTTP_ERROR(error);
  }
  @Action
  clearHttpError() {
    this.CLEAR_HTTP_ERROR();
  }

  @Action
  toggleIsDrawerOpen() {
    this.TOGGLE_IS_DRAWER_OPEN();
  }
  @Action
  setIsDrawerOpen(isOpen: boolean) {
    this.SET_IS_DRAWER_OPEN(isOpen);
  }
  @Action
  closeDrawer() {
    if (this.isDrawerOpen) {
      this.SET_IS_DRAWER_OPEN(false);
    }
  }

  @Action
  toggleIsSidenavMini() {
    this.TOGGLE_IS_SIDENAV_MINI();
  }
  @Action
  setIsSidenavMini(isMini: boolean) {
    this.SET_IS_SIDENAV_MINI(isMini);
  }
  @Action
  minifySidenav() {
    if (!this.isSidenavMini) {
      this.SET_IS_SIDENAV_MINI(true);
    }
  }
}
